// Types and Data for Growth Mindset survey
// Inspired by Carol Dweck's mindset research - original implementation for GROWNi.sk

import { v4 as uuidv4 } from 'uuid'
import type { Timestamp } from '../firebase.ts'
import type { UserId } from '../ids.ts'
import { SurveyTypes } from '../Survey.ts'
import { SurveyQuestionTypes } from './SelfKnowledgeSurvey.ts'

export type GrowthMindsetSurveyId = string

export interface GrowthMindsetSurvey {
    id: GrowthMindsetSurveyId
    surveyType: SurveyTypes.GROWTH_MINDSET
    userId: UserId
    hash?: string
    questions: GrowthMindsetQuestionType[] // Likert scale questions
    scenarios?: GrowthMindsetScenario[] // Behavioral scenarios
    allItems?: GrowthMindsetSurveyItem[] // Combined for display (questions + scenarios shuffled)
    scores: GrowthMindsetScores
    overallMindset: MindsetType
    domainScores: DomainScores // 0-100 per domain
    confidence: number // 0-100
    isDraft?: boolean
    createdAt: Timestamp
    updatedAt?: Timestamp
    deletedAt?: Timestamp | null
}

// Mindset classification
export enum MindsetType {
    FIXED = 'fixed', // Fixné myslenie
    MIXED = 'mixed', // Zmiešané myslenie
    GROWTH = 'growth' // Rastové myslenie
}

// Domains for domain-specific mindset
export enum MindsetDomain {
    INTELLIGENCE = 'intelligence', // Inteligencia
    TALENT = 'talent', // Talent/Schopnosti
    RELATIONSHIPS = 'relationships', // Vzťahy
    CAREER = 'career', // Kariéra
    HEALTH = 'health' // Zdravie
}

export const MindsetTypeLabels: Record<MindsetType, string> = {
    [MindsetType.FIXED]: 'Fixné myslenie',
    [MindsetType.MIXED]: 'Zmiešané myslenie',
    [MindsetType.GROWTH]: 'Rastové myslenie'
}

export const MindsetTypeDescriptions: Record<MindsetType, string> = {
    [MindsetType.FIXED]:
        'Veríš, že schopnosti sú dané a nedajú sa výrazne zmeniť. Vyhýbaš sa výzvam a vzdávaš sa ľahšie.',
    [MindsetType.MIXED]:
        'V niektorých oblastiach veríš v rozvoj, v iných si myslíš, že je to dané. Závisí to od situácie.',
    [MindsetType.GROWTH]:
        'Veríš, že sa môžeš vyvíjať a zlepšovať prostredníctvom úsilia a učenia. Výzvy berieš ako príležitosti.'
}

export const MindsetDomainLabels: Record<MindsetDomain, string> = {
    [MindsetDomain.INTELLIGENCE]: 'Inteligencia',
    [MindsetDomain.TALENT]: 'Talent a schopnosti',
    [MindsetDomain.RELATIONSHIPS]: 'Vzťahy',
    [MindsetDomain.CAREER]: 'Kariéra',
    [MindsetDomain.HEALTH]: 'Zdravie'
}

export interface GrowthMindsetQuestionType {
    id: string
    domain: MindsetDomain
    title: string // The mindset statement
    type: SurveyQuestionTypes
    scale: 1 | 2 | 3 | 4 | 5 // Agreement scale: 1=Strongly disagree, 5=Strongly agree
    isReversed: boolean // True if statement reflects fixed mindset
    value?: number // 1-5
}

// Behavioral scenario option
export interface GrowthMindsetScenarioOption {
    id: string
    text: string
    mindset: 'fixed' | 'mixed' | 'growth'
}

// Behavioral scenario - reveals actual response to setback
export interface GrowthMindsetScenario {
    id: string
    domain: MindsetDomain
    type: 'scenario' // Distinguishes from scale questions
    scenario: string // The setback situation description
    question: string // What do you do? / How do you react?
    options: GrowthMindsetScenarioOption[]
    selectedOptionId?: string // User's selected option
}

// Union type for all items in the survey
export type GrowthMindsetSurveyItem = GrowthMindsetQuestionType | GrowthMindsetScenario

// Type guard to check if item is a scenario
export function isScenario(item: GrowthMindsetSurveyItem): item is GrowthMindsetScenario {
    return 'type' in item && item.type === 'scenario'
}

export interface GrowthMindsetScores {
    [MindsetDomain.INTELLIGENCE]: number // Average score
    [MindsetDomain.TALENT]: number
    [MindsetDomain.RELATIONSHIPS]: number
    [MindsetDomain.CAREER]: number
    [MindsetDomain.HEALTH]: number
}

export interface DomainScores {
    [MindsetDomain.INTELLIGENCE]: number // 0-100
    [MindsetDomain.TALENT]: number
    [MindsetDomain.RELATIONSHIPS]: number
    [MindsetDomain.CAREER]: number
    [MindsetDomain.HEALTH]: number
}

// 15 questions - 3 per domain
export const GROWTH_MINDSET_QUESTIONS: GrowthMindsetQuestionType[] = [
    // INTELLIGENCE - 3 questions
    {
        id: uuidv4(),
        domain: MindsetDomain.INTELLIGENCE,
        type: SurveyQuestionTypes.SCALE,
        title: 'Moja inteligencia je daná a nemôžem ju výrazne zmeniť',
        scale: 5,
        isReversed: true,
        value: undefined
    },
    {
        id: uuidv4(),
        domain: MindsetDomain.INTELLIGENCE,
        type: SurveyQuestionTypes.SCALE,
        title: 'Môžem sa stať múdrejším/múdrejšou vďaka učeniu a cvičeniu mozgu',
        scale: 5,
        isReversed: false,
        value: undefined
    },
    {
        id: uuidv4(),
        domain: MindsetDomain.INTELLIGENCE,
        type: SurveyQuestionTypes.SCALE,
        title: 'Keď sa stretnem s ťažkou úlohou, radšej sa jej vyhnem, aby som nevyzerala/nevyzeral hlúpo',
        scale: 5,
        isReversed: true,
        value: undefined
    },

    // TALENT - 3 questions
    {
        id: uuidv4(),
        domain: MindsetDomain.TALENT,
        type: SurveyQuestionTypes.SCALE,
        title: 'Buď mám talent na niečo, alebo nie - nemôžem to zmeniť',
        scale: 5,
        isReversed: true,
        value: undefined
    },
    {
        id: uuidv4(),
        domain: MindsetDomain.TALENT,
        type: SurveyQuestionTypes.SCALE,
        title: 'S dostatočným úsilím a praxou môžem rozvinúť takmer akúkoľvek schopnosť',
        scale: 5,
        isReversed: false,
        value: undefined
    },
    {
        id: uuidv4(),
        domain: MindsetDomain.TALENT,
        type: SurveyQuestionTypes.SCALE,
        title: 'Ak v niečom nie som dobrý/dobrá od začiatku, nemá zmysel s tým pokračovať',
        scale: 5,
        isReversed: true,
        value: undefined
    },

    // RELATIONSHIPS - 3 questions
    {
        id: uuidv4(),
        domain: MindsetDomain.RELATIONSHIPS,
        type: SurveyQuestionTypes.SCALE,
        title: 'Kvalita vzťahov je daná - buď spolu vychádzame, alebo nie',
        scale: 5,
        isReversed: true,
        value: undefined
    },
    {
        id: uuidv4(),
        domain: MindsetDomain.RELATIONSHIPS,
        type: SurveyQuestionTypes.SCALE,
        title: 'Vzťahy sa dajú zlepšiť prácou, komunikáciou a porozumením',
        scale: 5,
        isReversed: false,
        value: undefined
    },
    {
        id: uuidv4(),
        domain: MindsetDomain.RELATIONSHIPS,
        type: SurveyQuestionTypes.SCALE,
        title: 'Keď mám konflikt s niekým, je to znamenie, že sme nezlučiteľní',
        scale: 5,
        isReversed: true,
        value: undefined
    },

    // CAREER - 3 questions
    {
        id: uuidv4(),
        domain: MindsetDomain.CAREER,
        type: SurveyQuestionTypes.SCALE,
        title: 'Moja kariéra je určená mojimi vrodenými schopnosťami',
        scale: 5,
        isReversed: true,
        value: undefined
    },
    {
        id: uuidv4(),
        domain: MindsetDomain.CAREER,
        type: SurveyQuestionTypes.SCALE,
        title: 'Môžem sa naučiť nové zručnosti a zmeniť svoju kariérnu cestu',
        scale: 5,
        isReversed: false,
        value: undefined
    },
    {
        id: uuidv4(),
        domain: MindsetDomain.CAREER,
        type: SurveyQuestionTypes.SCALE,
        title: 'Neúspech v práci znamená, že na túto pozíciu nemám',
        scale: 5,
        isReversed: true,
        value: undefined
    },

    // HEALTH - 3 questions
    {
        id: uuidv4(),
        domain: MindsetDomain.HEALTH,
        type: SurveyQuestionTypes.SCALE,
        title: 'Moje zdravie a kondícia sú dané geneticky',
        scale: 5,
        isReversed: true,
        value: undefined
    },
    {
        id: uuidv4(),
        domain: MindsetDomain.HEALTH,
        type: SurveyQuestionTypes.SCALE,
        title: 'Môžem zlepšiť svoje zdravie a kondíciu cez cvičenie a zdravý životný štýl',
        scale: 5,
        isReversed: false,
        value: undefined
    },
    {
        id: uuidv4(),
        domain: MindsetDomain.HEALTH,
        type: SurveyQuestionTypes.SCALE,
        title: 'Ak nie som prirodzene fit, nemá zmysel cvičiť',
        scale: 5,
        isReversed: true,
        value: undefined
    }
]

// 10 Behavioral scenarios - 2 per domain
// These reveal actual responses to setbacks, not just stated beliefs
export const GROWTH_MINDSET_SCENARIOS: GrowthMindsetScenario[] = [
    // INTELLIGENCE - 2 scenarios
    {
        id: uuidv4(),
        domain: MindsetDomain.INTELLIGENCE,
        type: 'scenario',
        scenario:
            'Na pohovore dostaneš logický test. Prvé 3 úlohy vyriešiš ľahko, ale pri 4. sa zasekneš. Čas beží, HR-ka sa pozerá. Po 5 minútach stále nevieš. Cítiš, ako ti stúpa teplo do tváre.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Tipnem odpoveď a pôjdem ďalej - asi na toto nemám', mindset: 'fixed' },
            { id: uuidv4(), text: 'Skúsim ešte chvíľu, ale ak to nepôjde, vzdám to', mindset: 'mixed' },
            { id: uuidv4(), text: 'Požiadam o papier a skúsim to rozložiť na menšie kroky', mindset: 'growth' }
        ]
    },
    {
        id: uuidv4(),
        domain: MindsetDomain.INTELLIGENCE,
        type: 'scenario',
        scenario:
            'Kolega Martin ti vysvetľuje nový systém už tretíkrát. Stále tomu úplne nerozumieš a vidíš, že je už trochu netrpezlivý. Ostatní kolegovia to pochopili na prvýkrát.',
        question: 'Ako zareaguješ?',
        options: [
            { id: uuidv4(), text: 'Poviem, že už rozumiem, a nejako to zvládnem sám/sama', mindset: 'fixed' },
            { id: uuidv4(), text: 'Požiadam ho, aby mi to ukázal ešte raz, ale cítim sa trápne', mindset: 'mixed' },
            {
                id: uuidv4(),
                text: 'Spýtam sa na konkrétnu časť, ktorú nechápem, alebo nájdem dokumentáciu',
                mindset: 'growth'
            }
        ]
    },

    // TALENT - 2 scenarios
    {
        id: uuidv4(),
        domain: MindsetDomain.TALENT,
        type: 'scenario',
        scenario:
            'Začal/a si sa učiť hrať na gitare. Po 4 mesiacoch cvičenia stále robíš základné chyby. Tvoj učiteľ ti povie: "Niektorí ľudia to proste majú v krvi a niektorí nie. Možno by si mal/a skúsiť niečo iné."',
        question: 'Ako sa k tomu postavíš?',
        options: [
            { id: uuidv4(), text: 'Asi mal pravdu, nemám na to talent - nechám to', mindset: 'fixed' },
            { id: uuidv4(), text: 'Možno by som mal/a skúsiť iný nástroj alebo koníček', mindset: 'mixed' },
            { id: uuidv4(), text: 'Nájdem si iného učiteľa alebo zmením metódu cvičenia', mindset: 'growth' }
        ]
    },
    {
        id: uuidv4(),
        domain: MindsetDomain.TALENT,
        type: 'scenario',
        scenario:
            'Na kurze kresby tvoja práca vyzerá výrazne horšie ako práce ostatných. Lektor povie: "Každý máme iné nadanie." Pri odchode si všimneš, že niekto sa na tvoj obrázok kriticky pozrie.',
        question: 'Čo si pomyslíš?',
        options: [
            { id: uuidv4(), text: 'Nie som kreatívny typ, na ďalší kurz už nepôjdem', mindset: 'fixed' },
            { id: uuidv4(), text: 'Kreslenie asi nie je pre mňa, skúsim niečo jednoduchšie', mindset: 'mixed' },
            {
                id: uuidv4(),
                text: 'Spýtam sa lektora na tipy, čo môžem zlepšiť, a budem cvičiť doma',
                mindset: 'growth'
            }
        ]
    },

    // RELATIONSHIPS - 2 scenarios
    {
        id: uuidv4(),
        domain: MindsetDomain.RELATIONSHIPS,
        type: 'scenario',
        scenario:
            'S partnerom/partnerkou máte opakované konflikty o tom istom - už tretíkrát tento mesiac. Po hádke ti povie: "Proste sme iní ľudia. Nikdy sa v tomto nezhodneme. Možno to tak má byť."',
        question: 'Ako zareaguješ?',
        options: [
            { id: uuidv4(), text: 'Má pravdu, v tomto sme asi naozaj nezlučiteľní', mindset: 'fixed' },
            { id: uuidv4(), text: 'Možno by sme mali túto tému radšej menej riešiť', mindset: 'mixed' },
            {
                id: uuidv4(),
                text: 'Skúsme to inak - možno potrebujeme pomoc zvonku alebo nový spôsob komunikácie',
                mindset: 'growth'
            }
        ]
    },
    {
        id: uuidv4(),
        domain: MindsetDomain.RELATIONSHIPS,
        type: 'scenario',
        scenario:
            'Priateľstvo s Petrom, ktoré trvalo 10 rokov, sa ti postupne odcudzuje. Odpovedá na správy neskoro, stretnutia stále odkladá. Pri poslednom telefonáte bol chladný a odmeraný.',
        question: 'Ako to vnímaš?',
        options: [
            { id: uuidv4(), text: 'Niektoré priateľstvá proste končia, nedá sa s tým nič robiť', mindset: 'fixed' },
            { id: uuidv4(), text: 'Počkám, či sa situácia sama zlepší - nechcem tlačiť', mindset: 'mixed' },
            {
                id: uuidv4(),
                text: 'Zavolám mu a otvorene sa opýtam, či je všetko v poriadku a čo môžem urobiť',
                mindset: 'growth'
            }
        ]
    },

    // CAREER - 2 scenarios
    {
        id: uuidv4(),
        domain: MindsetDomain.CAREER,
        type: 'scenario',
        scenario:
            'Tvoj projekt v práci zlyhal. Šéf ti na osobnom stretnutí povie: "Toto evidentne nie je tvoja silná stránka. Dám ti radšej úlohy, ktoré ti idú." Pred mesiacom ti pritom za tento projekt tlieskal.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Má pravdu, budem sa držať toho, čo viem', mindset: 'fixed' },
            { id: uuidv4(), text: 'Skúsim to ešte raz, ale ak to znova nepôjde, nechám to', mindset: 'mixed' },
            {
                id: uuidv4(),
                text: 'Poprosím ho o konkrétnu spätnú väzbu, čo presne nevyšlo, a navrhnem, ako to skúsim inak',
                mindset: 'growth'
            }
        ]
    },
    {
        id: uuidv4(),
        domain: MindsetDomain.CAREER,
        type: 'scenario',
        scenario:
            'Prišla ponuka povýšenia na pozíciu, o ktorej si sníval/a. Ale vyžaduje zručnosti, ktoré ešte nemáš - prezentovanie pred veľkým publikom a vedenie tímu. Máš 3 mesiace na to, aby si ich zvládol/a.',
        question: 'Ako sa rozhodneš?',
        options: [
            { id: uuidv4(), text: 'Odmietnem, nechcem riskovať zlyhanie a zosmiešnenie', mindset: 'fixed' },
            { id: uuidv4(), text: 'Prijmem a dúfam, že to bude nejako fungovať', mindset: 'mixed' },
            {
                id: uuidv4(),
                text: 'Prijmem a hneď si vytvorím plán, ako sa tie zručnosti naučím - kurzy, mentor, prax',
                mindset: 'growth'
            }
        ]
    },

    // HEALTH - 2 scenarios
    {
        id: uuidv4(),
        domain: MindsetDomain.HEALTH,
        type: 'scenario',
        scenario:
            'Po 2 mesiacoch behávania stále nevládzeš ani 2 km bez zastavenia. Kamarátka Eva, ktorá začala s tebou, už behá 5 km. Pri spoločnom behu ti povie: "Ty asi nie si bežecký typ. Skús radšej niečo iné."',
        question: 'Ako na to zareaguješ?',
        options: [
            { id: uuidv4(), text: 'Asi má pravdu, behanie jednoducho nie je pre mňa', mindset: 'fixed' },
            { id: uuidv4(), text: 'Budem radšej chodiť alebo plávať, to mi možno pôjde lepšie', mindset: 'mixed' },
            {
                id: uuidv4(),
                text: 'Možno potrebujem iný tréningový plán alebo viac času - každý má iné tempo',
                mindset: 'growth'
            }
        ]
    },
    {
        id: uuidv4(),
        domain: MindsetDomain.HEALTH,
        type: 'scenario',
        scenario:
            'Rozhodol/a si sa jesť zdravšie. Po týždni disciplíny si však v strese zjedol/a celú čokoládu a pizzu. Už sa ti to stalo tretíkrát za mesiac. Ráno sa cítiš previnilo.',
        question: 'Čo si povieš?',
        options: [
            {
                id: uuidv4(),
                text: 'Nemám dostatočnú vôľu, radšej to vzdám - nie som na diéty stavaný/á',
                mindset: 'fixed'
            },
            { id: uuidv4(), text: 'Skúsim to znova od pondelka, ale asi to nie je pre mňa', mindset: 'mixed' },
            {
                id: uuidv4(),
                text: 'Zistím, čo spúšťa moje "zlyhávania" a upravím stratégiu - možno potrebujem iný prístup',
                mindset: 'growth'
            }
        ]
    }
]

// Fisher-Yates shuffle algorithm
function shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        const left = shuffled[i]
        const right = shuffled[j]

        if (left === undefined || right === undefined) {
            continue
        }

        shuffled[i] = right
        shuffled[j] = left
    }
    return shuffled
}

// All 15 questions are used
function getQuestionSet(): GrowthMindsetQuestionType[] {
    return shuffleArray(GROWTH_MINDSET_QUESTIONS).map((q) => ({ ...q, value: undefined }))
}

// Get scenario set with shuffled options
function getScenarioSet(): GrowthMindsetScenario[] {
    return shuffleArray(GROWTH_MINDSET_SCENARIOS).map((s) => ({
        ...s,
        selectedOptionId: undefined,
        options: shuffleArray(s.options) // Shuffle options to prevent position bias
    }))
}

// Get all items combined and shuffled for display
function getAllItems(): GrowthMindsetSurveyItem[] {
    const questions = getQuestionSet()
    const scenarios = getScenarioSet()
    return shuffleArray([...questions, ...scenarios])
}

// Initialize a new Growth Mindset survey
export function initializedGrowthMindsetSurvey(userId: UserId): Omit<GrowthMindsetSurvey, 'id' | 'createdAt'> {
    const allItems = getAllItems()
    const questions = allItems.filter((item): item is GrowthMindsetQuestionType => !isScenario(item))
    const scenarios = allItems.filter((item): item is GrowthMindsetScenario => isScenario(item))

    return {
        surveyType: SurveyTypes.GROWTH_MINDSET,
        userId,
        hash: uuidv4(),
        questions,
        scenarios,
        allItems,
        scores: {
            [MindsetDomain.INTELLIGENCE]: 0,
            [MindsetDomain.TALENT]: 0,
            [MindsetDomain.RELATIONSHIPS]: 0,
            [MindsetDomain.CAREER]: 0,
            [MindsetDomain.HEALTH]: 0
        },
        overallMindset: MindsetType.MIXED,
        domainScores: {
            [MindsetDomain.INTELLIGENCE]: 0,
            [MindsetDomain.TALENT]: 0,
            [MindsetDomain.RELATIONSHIPS]: 0,
            [MindsetDomain.CAREER]: 0,
            [MindsetDomain.HEALTH]: 0
        },
        confidence: 0,
        isDraft: true,
        deletedAt: null
    }
}
